'use client';

import { useEffect, useRef, useState } from 'react';
import { Link } from '@/i18n/routing';
import { ArrowRight } from 'lucide-react';
import { useTranslations } from '@/i18n/intl';
import { useSettings } from '@/components/shared/SettingsProvider';
import { RichText } from '@/components/shared/RichText';
import { UploadedImage } from '@/components/shared/UploadedImage';

export function AboutSection() {
  const t = useTranslations('About');
  const settings = useSettings();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (window.location.hash !== '#about-us') return;
    const scrollToAbout = () => {
      document.getElementById('about-us')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    scrollToAbout();
    const timer = window.setTimeout(scrollToAbout, 100);
    return () => window.clearTimeout(timer);
  }, []);

  const homeAboutImage = settings.home_about_image;

  return (
    <section
      id="about-us"
      ref={sectionRef}
      className="py-6 md:py-12 px-5 md:px-[40px] bg-white overflow-hidden scroll-mt-28"
    >
      <div className="max-w-[1240px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center mb-6 md:mb-10">
          <div
            className={`lg:w-[55%] transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <span className="inline-block text-primary font-medium tracking-[0.2em] uppercase text-[12px] mb-6">
              {t('title')}
            </span>
            {settings.home_about_headline ? (
              <RichText
                html={settings.home_about_headline}
                className="text-xl md:text-2xl lg:text-[28px] font-semibold text-navy leading-[1.4] mb-8 font-heading tracking-tight [&_span]:text-primary [&_p]:mb-0"
              />
            ) : (
              <h2 className="text-xl md:text-2xl lg:text-[28px] font-semibold text-navy leading-[1.4] mb-8 font-heading tracking-tight">
                {t.rich('headline', {
                  blue: (chunks) => <span className="text-primary font-semibold">{chunks}</span>,
                })}
              </h2>
            )}

            {settings.home_about_description ? (
              <RichText
                html={settings.home_about_description}
                className="text-lg text-gray-500 leading-relaxed mb-12 max-w-[540px] font-sans font-light [&_p]:mb-4"
              />
            ) : (
              <p className="text-lg text-gray-500 leading-relaxed mb-12 max-w-[540px] font-sans font-light">
                {t('description')}
              </p>
            )}

            <Link
              href="/about-us"
              className="group inline-flex items-center gap-3 bg-navy text-white text-[13px] font-bold uppercase px-10 py-5 rounded-full hover:bg-primary transition-all duration-500 shadow-lg shadow-navy/10"
            >
              {t('button')}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          <div
            className={`lg:w-[45%] relative w-full transition-all duration-1000 delay-300 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          >
            <div className="relative aspect-square max-w-[520px] mx-auto rounded-2xl overflow-hidden bg-gray-100 shadow-xl border border-gray-100">
              {homeAboutImage ? (
                <UploadedImage
                  src={homeAboutImage}
                  alt="About Simisi&Co."
                  fill
                  className="object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-navy to-primary/80 flex items-center justify-center p-12">
                  <p className="text-white/60 text-sm font-sans text-center">
                    Upload image in Admin → About Settings → Homepage About Image
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
